import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  TrendingUp,
  CheckCircle,
  Clock,
  DollarSign,
  LogOut,
  Search,
  Trash2,
  Tag,
  Save,
  Download,
  Calendar,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Users,
  CreditCard,
  ExternalLink,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { exportOrdersToCSV } from '../utils/exportCSV';
import AdminHeader from '../components/AdminHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' ou 'prices'
  const [editingPrice, setEditingPrice] = useState(null);
  const [editingQuantity, setEditingQuantity] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const navigate = useNavigate();

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/backoffice/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, pricesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/admin/prices`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats(statsRes.data.data);
      setOrders(ordersRes.data.data);
      setPrices(pricesRes.data.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/backoffice/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, field, value) => {
    try {
      await axios.patch(
        `${API_URL}/admin/orders/${orderId}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      alert('Erro ao atualizar pedido');
    }
  };

  const handleMarkAsPaid = async (order) => {
    if (!window.confirm(`Marcar pedido ${order.orderCode} como PAGO?\nSerá enviado email de confirmação ao cliente.`)) return;
    try {
      await axios.post(
        `${API_URL}/admin/orders/${order.id}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      alert('Erro ao marcar como pago');
    }
  };

  const handleMarkAsDelivered = async (order) => {
    if (!window.confirm(`Marcar pedido ${order.orderCode} como ENTREGUE?\nSerá enviado email de entrega ao cliente.`)) return;
    try {
      await axios.post(
        `${API_URL}/admin/orders/${order.id}/mark-delivered`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      alert('Erro ao marcar como entregue');
    }
  };

  const handleDeliverySms = async (order) => {
    if (!window.confirm(`Enviar SMS de entrega para ${order.customerName}?`)) return;
    try {
      const res = await axios.post(
        `${API_URL}/admin/orders/${order.id}/delivery-sms`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const sentAt = res.data.sentAt || new Date().toISOString();
      setOrders(prev => prev.map(o => o.id !== order.id ? o : {
        ...o,
        remindersSent: { ...(o.remindersSent || {}), deliverySms: sentAt },
      }));
      alert('SMS de entrega enviado!');
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao enviar SMS');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Tem a certeza que deseja eliminar este pedido?')) {
      try {
        await axios.delete(
          `${API_URL}/admin/orders/${orderId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchData(); // Recarregar dados
        alert('Pedido eliminado com sucesso');
      } catch (error) {
        alert('Erro ao eliminar pedido');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/backoffice/login');
  };

  // Funções auxiliares para estatísticas avançadas
  const getOrdersToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return orders.filter(order => new Date(order.createdAt) >= today).length;
  };

  const getOrdersThisWeek = () => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return orders.filter(order => new Date(order.createdAt) >= weekAgo).length;
  };

  const getRevenueThisMonth = () => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    return orders
      .filter(order => new Date(order.createdAt) >= monthStart && order.paymentStatus === 'paid')
      .reduce((sum, order) => sum + parseFloat(order.price), 0);
  };

  // Filtro por data
  const filterByDate = (order) => {
    if (dateFilter === 'all') return true;

    const orderDate = new Date(order.createdAt);
    const now = new Date();

    if (dateFilter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return orderDate >= today;
    }

    if (dateFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return orderDate >= weekAgo;
    }

    if (dateFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return orderDate >= monthAgo;
    }

    return true;
  };

  // Filtrar pedidos
  const filteredOrders = orders.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.orderStatus === filterStatus;
    const s = searchTerm.trim().toLowerCase().replace(/^@/, '');
    const sPhone = s.replace(/\s+/g, '');
    const matchesSearch = !searchTerm ||
                          order.orderCode.toLowerCase().includes(s) ||
                          order.customerName.toLowerCase().includes(s) ||
                          order.customerEmail.toLowerCase().includes(s) ||
                          (order.customerPhone && order.customerPhone.replace(/\s+/g, '').includes(sPhone)) ||
                          (order.mbwayPhone && order.mbwayPhone.replace(/\s+/g, '').includes(sPhone)) ||
                          (order.username && order.username.toLowerCase().includes(s)) ||
                          (order.items && order.items.some(item => item.username.toLowerCase().includes(s)));
    const matchesDate = filterByDate(order);

    return matchesStatus && matchesSearch && matchesDate;
  });

  // Paginação
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const getDisplayName = (username) => {
    if (!username) return '';
    if (username.startsWith('http://') || username.startsWith('https://')) {
      try {
        const clean = username.trim().replace(/\/$/, '');
        const parts = clean.split('/').filter(Boolean);
        return parts[parts.length - 1] || username;
      } catch { return username; }
    }
    return username;
  };

  // Função para exportar
  const getProfileUrl = (platform, username) => {
    if (!platform || !username) return null;
    const u = username.replace(/^@/, '');
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    switch (platform.toLowerCase()) {
      case 'instagram': return `https://www.instagram.com/${u}`;
      case 'tiktok':    return `https://www.tiktok.com/@${u}`;
      case 'youtube':   return `https://www.youtube.com/@${u}`;
      case 'facebook':  return `https://www.facebook.com/${u}`;
      default:          return null;
    }
  };

  const handleExportCSV = () => {
    if (filteredOrders.length === 0) {
      alert('Não há pedidos para exportar');
      return;
    }
    exportOrdersToCSV(filteredOrders);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl font-bold text-gray-600">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader activePage="dashboard" activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="container mx-auto px-4 py-8">

        {/* Conteúdo das Tabs */}
        {activeTab === 'orders' && (
          <>
            {/* Filtros e Pesquisa */}
            <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
          <div className="grid md:grid-cols-2 gap-4 mb-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Pesquisar por nome, email, @username, telefone ou #ID..."
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-600 outline-none"
              />
            </div>

            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-600 outline-none appearance-none"
              >
                <option value="all">Todas as Datas</option>
                <option value="today">Hoje</option>
                <option value="week">Última Semana</option>
                <option value="month">Último Mês</option>
              </select>
            </div>
          </div>

          {/* Filtro de Status — pills */}
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {[
              { value: 'all',        label: 'Todos',          style: { active: 'bg-gray-800 text-white', idle: 'bg-gray-100 text-gray-600 hover:bg-gray-200' } },
              { value: 'processing', label: '⚙️ A Processar', style: { active: 'bg-yellow-500 text-white', idle: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' } },
              { value: 'completed',  label: '✅ Entregue',    style: { active: 'bg-green-600 text-white',  idle: 'bg-green-50 text-green-700 hover:bg-green-100' } },
              { value: 'pending',    label: '🕐 Pendente',    style: { active: 'bg-gray-500 text-white',  idle: 'bg-gray-100 text-gray-600 hover:bg-gray-200' } },
            ].map(({ value, label, style }) => (
              <button
                key={value}
                onClick={() => { setFilterStatus(value); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filterStatus === value ? style.active + ' border-transparent' : style.idle + ' border-gray-200'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Informações dos Resultados */}
          <div className="text-sm text-gray-600 mt-4">
            Mostrando {currentOrders.length} de {filteredOrders.length} pedidos
            {dateFilter !== 'all' && <span className="font-semibold"> ({dateFilter === 'today' ? 'Hoje' : dateFilter === 'week' ? 'Última Semana' : 'Último Mês'})</span>}
          </div>
        </div>

        {/* Lista de Pedidos */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-28">#ID</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-40">Cliente</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-28">Telefone</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700">Detalhes do Pedido</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-16">Valor</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-24">Pagamento</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-28">Status Pedido</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-28">Status Pag.</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-24">Data/Hora</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 w-36">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentOrders.length > 0 ? currentOrders.map((order) => {
                  // Verificar se é pedido com múltiplos itens (carrinho)
                  const isCartOrder = order.items && Array.isArray(order.items);

                  return (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const count = orders.filter(o =>
                              o.customerEmail === order.customerEmail ||
                              (order.customerPhone && o.customerPhone === order.customerPhone)
                            ).length;
                            return count > 1 ? (
                              <span className="text-[10px] font-bold bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full">{count}</span>
                            ) : null;
                          })()}
                          <span className="text-xs font-medium text-gray-800">{order.orderCode}</span>
                          <a
                            href={`/backoffice/order/${order.orderCode}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-400 hover:text-purple-600 transition"
                            title="Ver pedido"
                          >
                            <ExternalLink size={15} />
                          </a>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-xs font-medium text-gray-900">{order.customerName}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[140px]">{order.customerEmail}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-xs text-gray-900">{order.customerPhone}</div>
                        {order.mbwayPhone && order.paymentMethod === 'mbway' && (
                          <div className="flex items-center gap-1 text-xs text-purple-600 mt-0.5">
                            {order.mbwayPhone}
                            {order.mbwayPhone.replace(/\D/g, '') === order.customerPhone?.replace(/\D/g, '')
                              ? <span title="Igual ao nº do cliente">✅</span>
                              : <span title="Diferente do nº do cliente">⚠️</span>
                            }
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {isCartOrder ? (
                          // Pedido com múltiplos itens
                          <div className="space-y-2">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="text-xs border-l-2 border-purple-400 pl-2 py-1">
                                <div className="font-semibold text-purple-700 capitalize">{item.platform}</div>
                                <div className="flex items-center gap-1 text-gray-700">
                                  <span>@{getDisplayName(item.username)}</span>
                                  {getProfileUrl(item.platform, item.username) && (
                                    <a
                                      href={getProfileUrl(item.platform, item.username)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={e => e.stopPropagation()}
                                      className="text-gray-400 hover:text-purple-600 transition"
                                      title="Ver perfil"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>
                                <div className="text-gray-600">
                                  {item.quantity.toLocaleString('fr-FR')}{' '}
                                  {item.serviceType === 'likes' ? 'Likes' : item.serviceType === 'views' ? 'Views' : 'Seguidores'}
                                  {' - '}€{parseFloat(item.price).toFixed(2)}
                                </div>
                                {/* Posts Selecionados para este item */}
                                {item.selectedPosts && item.selectedPosts.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-purple-200">
                                    <div className="font-semibold text-purple-600 mb-1">📸 Posts ({item.selectedPosts.length}):</div>
                                    {item.selectedPosts.map((post, postIdx) => (
                                      <div key={postIdx} className="ml-2 mb-1 text-gray-600">
                                        <a
                                          href={post.postUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 hover:underline"
                                        >
                                          🔗 Post {postIdx + 1}
                                        </a>
                                        <span className="text-gray-500"> - {post.quantity} {item.serviceType === 'likes' ? 'likes' : 'views'}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                            <div className="text-xs font-bold text-gray-500 pt-1">
                              Total: {order.items.length} {order.items.length === 1 ? 'item' : 'itens'}
                            </div>
                          </div>
                        ) : (
                          // Pedido único
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 capitalize">
                                {order.platform}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-gray-900">@{getDisplayName(order.username)}</span>
                              {getProfileUrl(order.platform, order.username) && (
                                <a
                                  href={getProfileUrl(order.platform, order.username)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="text-gray-400 hover:text-purple-600 transition"
                                  title="Ver perfil"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                            <div className="text-xs text-gray-600">{order.packageType}</div>

                            {/* Posts Selecionados para pedido único */}
                            {order.selectedPosts && order.selectedPosts.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-purple-200">
                                <div className="font-semibold text-purple-600 mb-1 text-xs">📸 Posts ({order.selectedPosts.length}):</div>
                                {order.selectedPosts.map((post, postIdx) => (
                                  <div key={postIdx} className="ml-2 mb-1 text-xs">
                                    <a
                                      href={post.postUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                      🔗 Post {postIdx + 1}
                                    </a>
                                    <span className="text-gray-500"> - {post.quantity} {order.serviceType === 'likes' ? 'likes' : 'views'}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs font-bold text-gray-900 whitespace-nowrap">€{parseFloat(order.price).toFixed(2)}</td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 whitespace-nowrap">
                          {order.paymentMethod === 'mbway' && 'MB WAY'}
                          {order.paymentMethod === 'multibanco' && 'Multibanco'}
                          {order.paymentMethod === 'card' && 'Cartão'}
                          {order.paymentMethod === 'paypal' && 'PayPal'}
                          {!['mbway', 'multibanco', 'card', 'paypal'].includes(order.paymentMethod) && order.paymentMethod}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {order.orderStatus === 'completed' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-400 whitespace-nowrap">✅ Entregue</span>
                        ) : order.orderStatus === 'processing' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap">⚙️ A processar</span>
                        ) : order.orderStatus === 'cancelled' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 whitespace-nowrap">❌ Cancelado</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-500 whitespace-nowrap">🕐 Pendente</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {order.paymentStatus === 'paid' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">💰 Pago</span>
                        ) : order.paymentStatus === 'failed' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 whitespace-nowrap">❌ Falhado</span>
                        ) : order.paymentStatus === 'refunded' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap">↩️ Reembolso</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600 whitespace-nowrap">🕐 Pendente</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                        <div>{new Date(order.createdAt).toLocaleDateString('pt-PT')}</div>
                        <div className="text-xs text-gray-400">
                          {new Date(order.createdAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex flex-col gap-2">
                          {order.paymentStatus === 'pending' && (
                            <button
                              onClick={() => handleMarkAsPaid(order)}
                              className="flex items-center gap-1 bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition font-medium text-xs whitespace-nowrap"
                            >
                              ✅ Marcar como Pago
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            className="flex items-center gap-1 bg-red-100 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-200 transition font-medium text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="10" className="text-center py-12 text-gray-500">
                      Nenhum pedido encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {filteredOrders.length > 0 && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-700">
                Página <span className="font-semibold">{currentPage}</span> de <span className="font-semibold">{totalPages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Próxima
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
        </>
        )}

        {/* Tab de Preços */}
        {activeTab === 'prices' && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Gestão de Preços</h2>
              <p className="text-gray-600 mt-2">Atualize os preços dos pacotes por tipo de serviço</p>
            </div>

            {/* Filtros por Plataforma e Serviço */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Filtro por Plataforma */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Plataforma</h3>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setFilterPlatform('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterPlatform === 'all'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Todas
                    </button>
                    <button
                      onClick={() => setFilterPlatform('instagram')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterPlatform === 'instagram'
                          ? 'bg-pink-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Instagram
                    </button>
                    <button
                      onClick={() => setFilterPlatform('tiktok')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterPlatform === 'tiktok'
                          ? 'bg-black text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      TikTok
                    </button>
                    <button
                      onClick={() => setFilterPlatform('youtube')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterPlatform === 'youtube'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      YouTube
                    </button>
                    <button
                      onClick={() => setFilterPlatform('facebook')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterPlatform === 'facebook'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Facebook
                    </button>
                  </div>
                </div>

                {/* Filtro por Serviço */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Tipo de Serviço</h3>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setFilterService('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterService === 'all'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Todos
                    </button>
                    <button
                      onClick={() => setFilterService('followers')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterService === 'followers'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      👥 Seguidores
                    </button>
                    <button
                      onClick={() => setFilterService('likes')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterService === 'likes'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      ❤️ Likes
                    </button>
                    <button
                      onClick={() => setFilterService('views')}
                      className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                        filterService === 'views'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      👁️ Visualizações
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Plataforma</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Serviço</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Quantidade</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Preço (€)</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {prices
                    .filter((price) =>
                      (filterPlatform === 'all' || price.platform === filterPlatform) &&
                      (filterService === 'all' || price.serviceType === filterService)
                    )
                    .map((price) => (
                    <tr key={price.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 capitalize">
                          {price.platform}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 capitalize">
                          {price.serviceType || 'followers'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {editingQuantity === price.id ? (
                          <input
                            type="number"
                            defaultValue={price.quantity}
                            id={`quantity-${price.id}`}
                            className="w-32 px-3 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:border-blue-600"
                          />
                        ) : (
                          <span className="text-sm font-bold text-gray-900">
                            {price.quantity.toLocaleString('fr-FR')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {editingPrice === price.id ? (
                          <input
                            type="number"
                            step="0.01"
                            defaultValue={price.price}
                            id={`price-${price.id}`}
                            className="w-32 px-3 py-2 border-2 border-purple-300 rounded-lg focus:outline-none focus:border-purple-600"
                          />
                        ) : (
                          <span className="text-lg font-bold text-gray-900">€{parseFloat(price.price).toFixed(2)}</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          price.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {price.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {editingPrice === price.id || editingQuantity === price.id ? (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    const updateData = {};

                                    if (editingPrice === price.id) {
                                      const newPrice = document.getElementById(`price-${price.id}`).value;
                                      updateData.price = parseFloat(newPrice);
                                    }

                                    if (editingQuantity === price.id) {
                                      const newQuantity = document.getElementById(`quantity-${price.id}`).value;
                                      updateData.quantity = parseInt(newQuantity);
                                    }

                                    await axios.put(
                                      `${API_URL}/admin/prices/${price.id}`,
                                      updateData,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    fetchData();
                                    setEditingPrice(null);
                                    setEditingQuantity(null);
                                  } catch (error) {
                                    console.error('Erro ao atualizar:', error);
                                    alert('Erro ao atualizar');
                                  }
                                }}
                                className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition font-medium text-sm"
                              >
                                <Save className="w-4 h-4" />
                                Guardar
                              </button>
                              <button
                                onClick={() => {
                                  setEditingPrice(null);
                                  setEditingQuantity(null);
                                }}
                                className="flex items-center gap-2 bg-gray-400 text-white px-3 py-2 rounded-lg hover:bg-gray-500 transition font-medium text-sm"
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingPrice(price.id)}
                                className="flex items-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition font-medium text-sm"
                              >
                                Preço
                              </button>
                              <button
                                onClick={() => setEditingQuantity(price.id)}
                                className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition font-medium text-sm"
                              >
                                Qtd
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {prices
              .filter((price) =>
                (filterPlatform === 'all' || price.platform === filterPlatform) &&
                (filterService === 'all' || price.serviceType === filterService)
              )
              .length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Nenhum preço encontrado para{' '}
                {filterPlatform !== 'all' && filterService !== 'all'
                  ? `${filterPlatform} - ${filterService}`
                  : filterPlatform !== 'all'
                  ? filterPlatform
                  : filterService !== 'all'
                  ? filterService
                  : 'estes filtros'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}